import React, { useSyncExternalStore } from "react";
import { modal } from '../js/alpine/components/utility/modal/modal';
import { lazyComponents } from "@src/utilities/LazyComponent";
import { useAnimation } from '@src/hooks/useAnimation';
import useColoredTextParser from '@src/hooks/useColoredTextParser';
import { useHubSpotForm } from '@src/hooks/useHubSpotForm';

TagListComponent.displayName = 'theme__block-tags';
export function TagListComponent({ ...props }) {
    const TagList = lazyComponents['TagList'];
    return (
        <TagList tags={props.settings.tags} />
    );
}

SocialLinksComponent.displayName = 'theme__social-links';
export function SocialLinksComponent({ ...props }) {
    const SocialLinks = lazyComponents['SocialLinks'];
    const social_links = props.settings.shop.social_links;
    const section_settings = props.settings.settings;

    const links = Object.keys(social_links).map(key => ({
        url: social_links[key],
        name: key
    }));

    return (
        <SocialLinks type={'default'} links={links} settings={section_settings} />
    );
}

ModalComponent.displayName = 'theme__block-modal';
export function ModalComponent({ ...props }) {
    const Modal = lazyComponents['Modal'];

    function closeModal() {
        modal.component().hide();
    }

    function getIsOpen() {
        return useSyncExternalStore(modal.component().subscribe, modal.component().getIsOpen);
    }

    return (
        <Modal modal_id={props.settings.id} isOpen={getIsOpen()} title={props.settings.title} description={props.settings.content} position={'right'} onClose={closeModal} />
    );
}

LogoComponent.displayName = 'theme__block-logo';
export function LogoComponent({ ...props }) {
    const Logo = lazyComponents['Logo'];
    return (
        <Logo settings={props.settings} />
    );
}

TitleComponent.displayName = 'theme__block-title';
export function TitleComponent({ ...props }) {
    const Title = lazyComponents['Title'];
    return (
        <Title settings={props.settings} />
    );
}

DescriptionComponent.displayName = 'theme__block-description';
export function DescriptionComponent({ ...props }) {
    const Description = lazyComponents['Description'];
    return (
        <Description settings={props.settings} />
    );
}

ButtonComponent.displayName = 'theme__block-button';
export function ButtonComponent({ ...props }) {
    const Button = lazyComponents['Button'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract button settings
    const buttonText = blockSettings.button_text || '';
    const buttonUrl = blockSettings.button_url || null;
    const buttonStyle = blockSettings.button_style || 'primary';
    const buttonSize = blockSettings.button_size || 'md';
    const buttonIcon = blockSettings.button_icon || '';
    const buttonReverseIcon = blockSettings.button_reverse_icon || false;
    const buttonFullWidth = blockSettings.button_full_width || false;
    const buttonType = blockSettings.button_type || 'button'; // Get button type (submit, reset, button)

    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 10
    } : {};
    
    const buttonStyleType = blockSettings.cta_style_type || 'button';
    
    // Extract event tracking settings
    const enableEventTracking = blockSettings.enable_event_tracking || false;
    const eventName = blockSettings.event_name || 'button_click';
    const eventCategory = blockSettings.event_category || 'interaction';
    const eventLabel = blockSettings.event_label || buttonText;
    const eventValue = blockSettings.event_value ? Number(blockSettings.event_value) : undefined;

    // HubSpot form settings
    const globals = props.settings.globals || {};
    const { openForm } = useHubSpotForm({
        portalId: globals.hubspot?.portal_id,
        formId: blockSettings.section_hubspot_form_id || globals.hubspot?.form_id,
        region: blockSettings.section_hubspot_region || globals.hubspot?.form_region
    });
    const isContactForm = blockSettings.use_form || false;
    
    // Function to handle click events and dispatch a CustomEvent
    const handleButtonClick = () => {
        if (isContactForm) {
            openForm();
            return;
        }

        if (enableEventTracking && typeof window !== 'undefined') {
            try {
                window.dispatchEvent(
                    new CustomEvent(eventName, {
                        detail: {
                            category: eventCategory,
                            label: eventLabel,
                            value: eventValue
                        }
                    })
                );
                // Optionally log for debugging
                // console.log('Dispatched event:', eventName, { category: eventCategory, label: eventLabel, value: eventValue });
            } catch (error) {
                console.error('Error dispatching button event:', error);
            }
        }
    };
    
    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    return (
        <div ref={animation.ref} style={animation.style}>
            <Button
                label={buttonText}
                link={isContactForm ? undefined : buttonUrl}
                style={buttonStyle}
                styleType={buttonStyleType}
                size={buttonSize}
                icon={buttonIcon}
                reverse={buttonReverseIcon}
                fullWidth={buttonFullWidth}
                isCircle={blockSettings.is_circle || false}
                onClick={() => handleButtonClick()}
                type={buttonType} // Pass the button type to the Button component
                {...positionProps}
            />
        </div>
    );
}

TextComponent.displayName = 'theme__block-text';
export function TextComponent({ ...props }) {
    const Paragraph = lazyComponents['Paragraph'];
    const blockId = props.settings.block_id;
    const blockData = props.settings.block_settings;

    const text = blockData.text;
    const style = blockData.text_style;
    const htmlTag = blockData.html_tag || 'div'; // Get the HTML tag for SEO
    const maxChars = blockData.max_chars ? parseInt(blockData.max_chars, 10) : 0; // Get max chars with fallback to 0 (unlimited)
    
    // Get text color settings
    const overrideTextColor = blockData.override_text_color || false;
    const textColor = blockData.text_color || '';
    
    // Get text accent part settings
    const enableAccentParts = blockData.enable_accent_parts || false;
    const accentParts = blockData.accent_title_parts || '';
    const accentColor = blockData.accent_color || '#f26823';

    // Extract positioning data for overlay support
    const positionType = blockData.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockData.z_index || 10
    } : {};
   
    // Animation settings
    const enableAnimations = blockData.enable_animations || false;
    const animationType = blockData.animation_type || 'slide-fade-up';
    const animationDuration = blockData.animation_duration || 800;
    const animationDelay = blockData.animation_delay || 0;
    const animationTrigger = blockData.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    // Define custom styles including text color if overridden
    const textStyles = overrideTextColor ? { color: textColor } : {};
    
    // Use the colored text parser if accent parts are enabled
    const renderFormattedText = useColoredTextParser({
        title: text,
        accentTitleParts: accentParts,
        accentColor: accentColor,
        headerStyle: style,
        classes: '', // Don't use classes for text color, use style instead
    });

    return (
        <div ref={animation.ref} style={animation.style}>
            {enableAccentParts ? (
                <div style={textStyles}>
                    {renderFormattedText()}
                </div>
            ) : (
                <Paragraph    
                    text={text}
                    type={style}
                    htmlTag={htmlTag}
                    maxChars={maxChars || 0} // If maxChars is 0, it will be treated as unlimited
                    shouldTruncateText={true}
                    style={textStyles}
                    {...positionProps}
                />
            )}
        </div>
    );
}

VideoComponent.displayName = 'theme__block-video';
export function VideoComponent({ ...props }) {
    const Video = lazyComponents['Video'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Get video URL from the Shopify video object
    const videoUrl = blockSettings.video_url ? blockSettings.video_url : blockSettings.video?.sources?.find(source => source.format === 'mp4')?.url || '';
    if (!videoUrl) {
        return null;
    }
    
    // Extract all video settings
    const sizingMode = blockSettings.sizing_mode || 'aspect_ratio';
    const aspectRatio = blockSettings.aspect_ratio || '16:9';
    const customHeight = blockSettings.custom_height || 0;
    const objectFit = blockSettings.object_fit || 'cover';
    const autoplay = blockSettings.autoplay || false;
    const loop = blockSettings.loop || false;
    const controls = blockSettings.controls !== false;
    const muted = blockSettings.muted !== false;
    const borderRadius = blockSettings.border_radius || 0;
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 5
    } : {};
    
    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    console.log('VideoComponent settings:', videoUrl);
    return (
        <div ref={animation.ref} style={animation.style}>
            <Video
                videoUrl={videoUrl}
                sizingMode={sizingMode}
                aspectRatio={aspectRatio}
                customHeight={customHeight}
                objectFit={objectFit}
                autoplay={autoplay}
                loop={loop}
                controls={controls}
                muted={muted}
                playsInline={true}
                borderRadius={borderRadius}
                {...positionProps}
            />
        </div>
    );
}

ImageComponent.displayName = 'theme__block-image';
export function ImageComponent({ ...props }) {
    const Image = lazyComponents['Image'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
    
    if (!blockSettings) {
        return null;
    }

    // Extract image settings
    const imageUrl = blockSettings.image;
    const imageFit = blockSettings.image_fit || 'cover';
    const aspectRatio = blockSettings.aspect_ratio || 'auto';
    const overlayOpacity = blockSettings.overlay_opacity || 0;
    const borderRadius = blockSettings.border_radius || 0;
    
    // Extract link settings
    const enableLink = blockSettings.enable_link || false;
    const link = blockSettings.link || '';
    const linkTarget = blockSettings.link_target || '_self';
    const hasLink = enableLink && link.trim() !== '';
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 5
    } : {};
    
    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });

    const imageComponent = (
        <Image
            imageUrl={imageUrl}
            fit={imageFit}
            ratio={aspectRatio}
            overlay={overlayOpacity > 0}
            overlayOpacity={overlayOpacity / 100}
            borderRadius={borderRadius}
            classes={blockSettings.width < 100 ? `w-[${blockSettings.width}%]` : 'w-full'}
            {...positionProps}
        />
    );

    return (
        <div ref={animation.ref} style={animation.style}>
            {hasLink ? (
                <a 
                    href={link} 
                    target={linkTarget}
                    rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                    className="block"
                >
                    {imageComponent}
                </a>
            ) : (
                imageComponent
            )}
        </div>
    );
}

GroupComponent.displayName = 'theme__block-group';
export function GroupComponent({ ...props }) {
    const blockId = props.settings.block_id;
    const blockData = props.settings.blocks.find((block) => block.id === blockId);
    const blockSettings = props.settings.block_settings || {};

    const size = blockSettings.size || {};
    const spacing = blockSettings.spacing || {};
    
    // Convert size and spacing to style object
    const styleObj = {
        width: size.width || '100%',
        height: size.height || 'auto',
        padding: spacing.padding || '0px',
        margin: spacing.margin || '0px'
    };

    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    // Find all child blocks within this group
    const childBlocks = blockData?.blocks || [];

    return (
        <div ref={animation.ref} style={{...styleObj, ...animation.style}} className="group-component">
            {childBlocks.map((block, index) => {
                // Render child blocks here
                return <div key={`block-${index}`}></div>;
            })}
        </div>
    );
}

InputComponent.displayName = 'theme__block-input';
export function InputComponent({ ...props }) {
    const InputField = lazyComponents['InputField'];
    const [inputValue, setInputValue] = React.useState('');
    const [debouncedValue, setDebouncedValue] = React.useState('');
    const debounceTimerRef = React.useRef(null);
    
    const blockId = props.settings.block_id;
    const blockData = props.settings.block_settings;
    
    if (!blockData) {
        return null;
    }
    
    // Extract input settings
    const inputType = blockData.input_type || 'text';
    const inputName = blockData.input_name || '';
    const inputId = blockData.input_id || '';
    const inputLabel = blockData.input_label || '';
    const inputPlaceholder = blockData.input_placeholder || '';
    const inputRequired = blockData.input_required === 'true' || blockData.input_required === true;
    const inputPattern = blockData.input_pattern || '';
    const inputOptions = blockData.input_options || '';
    
    // Event tracking settings
    const enableEventTracking = blockData.enable_event_tracking || false;
    const eventTypeMapping = {
        change: 'input_change',
        blur: 'input_blur',
        focus: 'input_focus',
        keypress: 'input_keypress',
        finished: 'input_finished'
    };
    const eventName = blockData.event_name || eventTypeMapping.finished;
    const eventCategory = blockData.event_category || 'interaction';
    const eventLabel = blockData.event_label || inputLabel || inputName;
    const eventValue = blockData.event_value ? Number(blockData.event_value) : undefined;
    const debounceDelay = parseInt(blockData.debounce_delay || 500, 10);
    
    // Function to handle input events and dispatch a CustomEvent with debounce
    const handleInputEvent = (eventType, value) => {
        if (!enableEventTracking) return;
        
        if (eventType === 'change') {
            // Update the current input value immediately
            setInputValue(value);
            
            // Clear any existing timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            
            // Set a new timer for the "finished typing" event
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedValue(value);
                
                // Only dispatch the "finished" event when the user has stopped typing
                if (typeof window !== 'undefined') {
                    try {
                        window.dispatchEvent(
                            new CustomEvent(eventName, {
                                detail: {
                                    type: eventCategory,
                                    label: eventLabel,
                                    query: value,
                                    inputName: inputName,
                                    inputId: inputId,
                                    eventType: 'finished'
                                }
                            })
                        );
                        // console.log('Dispatched input finished event:', eventName, { 
                        //     value: value,
                        //     inputName: inputName 
                        // });
                    } catch (error) {
                        console.error('Error dispatching input event:', error);
                    }
                }
            }, debounceDelay);
        } else {
            // For other events (blur, focus, keypress), dispatch immediately if configured
            const eventToDispatch = blockData[`enable_${eventType}_event`] ? 
                blockData[`${eventType}_event_name`] || eventTypeMapping[eventType] : null;
                
            if (eventToDispatch && typeof window !== 'undefined') {
                try {
                    window.dispatchEvent(
                        new CustomEvent(eventToDispatch, {
                            detail: {
                                category: blockData[`${eventType}_event_category`] || eventCategory,
                                label: blockData[`${eventType}_event_label`] || eventLabel,
                                value: value,
                                inputName: inputName,
                                inputId: inputId,
                                eventType: eventType
                            }
                        })
                    );
                } catch (error) {
                    console.error(`Error dispatching ${eventType} event:`, error);
                }
            }
        }
    };
    
    // Extract positioning data for overlay support
    const positionType = blockData.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockData.z_index || 10
    } : {};
    
    // Parse options for select/radio/checkbox
    let parsedOptions = [];
    if (inputOptions && ['select', 'radio', 'checkbox'].includes(inputType)) {
        parsedOptions = inputOptions.split('\n').map(option => {
            const parts = option.split('|');
            return {
                value: parts[0]?.trim() || '',
                label: parts[1]?.trim() || parts[0]?.trim() || ''
            };
        });
    }
    
    // For hidden fields, render a simple hidden input
    if (inputType === 'hidden') {
        return (
            <input 
                type="hidden" 
                name={inputName} 
                id={inputId} 
                value={inputValue} 
                data-hidden-field="true"
            />
        );
    }
    
    // Animation settings
    const enableAnimations = blockData.enable_animations || false;
    const animationType = blockData.animation_type || 'slide-fade-up';
    const animationDuration = blockData.animation_duration || 800;
    const animationDelay = blockData.animation_delay || 0;
    const animationTrigger = blockData.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    // Properties for handling input events
    const inputEventProps = {
        onChange: (e) => handleInputEvent('change', e.target.value),
        onBlur: (e) => handleInputEvent('blur', e.target.value),
        onFocus: (e) => handleInputEvent('focus', e.target.value),
        onKeyPress: (e) => handleInputEvent('keypress', e.target.value)
    };
    
    return (
        <div ref={animation.ref} style={animation.style}>
            <InputField
                type={inputType}
                name={inputName}
                id={inputId}
                label={inputLabel}
                placeholder={inputPlaceholder}
                defaultValue={inputValue}
                required={inputRequired}
                pattern={inputPattern}
                options={parsedOptions}
                eventHandlers={enableEventTracking ? inputEventProps : {}}
                {...positionProps}
            />
        </div>
    );
}

IconComponent.displayName = 'theme__block-icon';
export function IconComponent({ ...props }) {
    const Icon = lazyComponents['Icon'];

    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
        
    if (!blockSettings) {
        return null;
    }
  
    return (
        <Icon settings={blockSettings} />
    );
}

BreadcrumbsComponent.displayName = 'theme__block-breadcrumbs';
export function BreadcrumbsComponent({ ...props }) {
    const Breadcrumbs = lazyComponents['Breadcrumbs'];
    
    const blockSettings = props.settings.block_settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Create a section object from block settings to match the format expected by the Breadcrumbs component
    const section = {
        divider_type: blockSettings.divider_type || 'icon',
        divider_text: blockSettings.divider_text || '/',
        spacing: blockSettings.spacing || 8,
        spacing_mobile: blockSettings.spacing_mobile || 4,
        icon: blockSettings.icon || 'icon-caret-right-thin',
        icon_size: blockSettings.icon_size || 18,
        text_style: blockSettings.text_style || 'b2',
        text_style_mobile: blockSettings.text_style_mobile || 'b3',
        default_shop_url: blockSettings.default_shop_url || '',
        use_back_to_category_products: blockSettings.use_back_to_category_products || false,
        content_alignment: blockSettings.content_alignment || 'left'
    };
    
    // Create a settings object that matches what the Breadcrumbs component expects
    const breadcrumbSettings = {
        ...props.settings,
        section: section,
        mobile_breakpoint: props.settings.mobile_breakpoint || 768
    };
    
    return <Breadcrumbs settings={breadcrumbSettings} />;
}

AccordionComponent.displayName = 'theme__block-accordion';
export function AccordionComponent({ ...props }) {
    const Collapsible = lazyComponents['Collapsible'];
    const Paragraph = lazyComponents['Paragraph'];
    const Media = lazyComponents['Media'];
    const Button = lazyComponents['Button'];
    
    const blockSettings = props.settings.block_settings;
    const accordionItems = props.settings.blocks || [];
    
    if (!blockSettings) {
        return null;
    }
    
    const getAccordionItemClass = () => {
        return `${blockSettings.vertical_spacing ? `mb-[${blockSettings.vertical_spacing}px]` : 'mb-0'} ${blockSettings.rounded_corners ? `rounded-[${blockSettings.rounded_corners}px]` : ''}`;
    };
    
    // Find out if any accordion items have open_by_default set to true
    const hasDefaultOpenItem = accordionItems.some(item => item.settings?.open_by_default);

    // Render each accordion item - could be directly defined or a nested accordion_item block
    const renderAccordionItem = (item, index) => {
        // console.log('Rendering accordion item:', item);
        // Handle special case for accordion_item blocks
        if (item.type === 'accordion_item_block') {
            // Pass parent settings to the nested accordion item
            const nestedItemProps = {
                ...props,
                settings: {
                    ...props.settings,
                    block_settings: item.settings,
                    parentSettings: blockSettings
                }
            };
            
            // Render the nested accordion item component directly
            return (
                <AccordionItemComponent 
                    key={`accordion_item_${index}`} 
                    {...nestedItemProps} 
                />
            );
        }
        
        // Otherwise, render a standard accordion item from settings
        const titleAlignment = item.settings?.text_alignment || blockSettings.accordion_text_alignment || 'left';
        
        return (
            <Collapsible
                key={`accordion_item_${index}`}
                title={item.settings?.title}
                titleClass={`${blockSettings.title_font || 'bfb'} font-medium text-${titleAlignment}`}
                contentClass={`${blockSettings.content_font || 'b2'} text-${titleAlignment}`}
                openIcon={blockSettings.accordion_icon_open || "icon-caret-down-thin"}
                closeIcon={blockSettings.accordion_icon_close || "icon-caret-up-thin"}
                className={getAccordionItemClass()}
                background={blockSettings.accordion_background || "transparent"}
                readMore={blockSettings.show_read_more}
                selectedBackground={blockSettings.accordion_selected_background}
                selectedTextColor={blockSettings.accordion_selected_text_color}
                useLineBreak={item.settings?.use_line_break}
                defaultOpen={item.settings?.open_by_default || (!hasDefaultOpenItem && index === 0)}
                allowMultipleOpen={blockSettings.allow_multiple_open}
            >
                {(item.settings?.video || item.settings?.image) && (
                    <div className="accordion__content-media">
                        <Media
                            videoItem={item.settings?.video}
                            imageItem={item.settings?.image}
                            index={index}
                        />
                    </div>
                )}
                {item.settings?.content && (
                    <div className="accordion__content-text">
                        <Paragraph
                            text={item.settings.content}
                            type={blockSettings.content_font || "b2"}
                        />
                    </div>
                )}
                {item.settings?.button_text && item.settings?.button_url && (
                    <div className="accordion__content-button mt-4">
                        <Button
                            label={item.settings.button_text}
                            link={item.settings.button_url}
                            style={item.settings.button_style || "primary"}
                        />
                    </div>
                )}
            </Collapsible>
        );
    };

    return (
        <div className="accordion-block w-full">
            {accordionItems.map((item, index) => renderAccordionItem(item, index))}
        </div>
    );
}

AccordionItemComponent.displayName = 'theme__block-accordion-item';
export function AccordionItemComponent({ ...props }) {
    const Collapsible = lazyComponents['Collapsible'];
    const Paragraph = lazyComponents['Paragraph'];
    const Media = lazyComponents['Media'];
    const Button = lazyComponents['Button'];
    
    const blockSettings = props.settings.block_settings;
    
    // console.log('AccordionItemComponent props:', blockSettings);
    if (!blockSettings) {
        return null;
    }
    
    // Get parent accordion's settings if available - this would be from a parent component
    const parentSettings = props.settings.parentSettings || {};
    
    // Use item's text alignment or fallback to parent's or default left
    const titleAlignment = blockSettings.text_alignment || parentSettings.accordion_text_alignment || 'left';
    
    // Create the accordion item using the Collapsible component
    return (
        <Collapsible
            title={blockSettings.title}
            titleClass={`${parentSettings.title_font || 'bfb'} font-medium text-${titleAlignment}`}
            contentClass={`${parentSettings.content_font || 'b2'} text-${titleAlignment}`}
            openIcon={parentSettings.accordion_icon_open || "icon-caret-down-thin"}
            closeIcon={parentSettings.accordion_icon_close || "icon-caret-up-thin"}
            className={parentSettings.vertical_spacing ? `mb-[${parentSettings.vertical_spacing}px]` : 'mb-0'}
            background={parentSettings.accordion_background || "transparent"}
            readMore={parentSettings.show_read_more}
            selectedBackground={parentSettings.accordion_selected_background}
            selectedTextColor={parentSettings.accordion_selected_text_color}
            useLineBreak={blockSettings.use_line_break}
            defaultOpen={blockSettings.open_by_default}
            allowMultipleOpen={parentSettings.allow_multiple_open}
        >
            {(blockSettings.video || blockSettings.image) && (
                <div className="accordion__content-media">
                    <Media
                        videoItem={blockSettings.video}
                        imageItem={blockSettings.image}
                        index={0}
                    />
                </div>
            )}
            {blockSettings.content && (
                <div className="accordion__content-text">
                    <Paragraph
                        text={blockSettings.content}
                        type={parentSettings.content_font || "b2"}
                    />
                </div>
            )}
            {blockSettings.button_text && blockSettings.button_url && (
                <div className="accordion__content-button mt-4">
                    <Button
                        label={blockSettings.button_text}
                        link={blockSettings.button_url}
                        style={blockSettings.button_style || "primary"}
                    />
                </div>
            )}
        </Collapsible>
    );
}

CarouselComponent.displayName = 'theme__block-carousel';
export function CarouselComponent({ ...props }) {
    const Carousel = lazyComponents['Carousel'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
    const childBlocks = props.settings.blocks || [];
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract carousel settings
    const showNavigation = blockSettings.show_navigation !== false;
    const showPagination = blockSettings.show_pagination !== false;
    const autoplay = blockSettings.autoplay || false;
    const autoplaySpeed = blockSettings.autoplay_speed || 3000;
    const loop = blockSettings.loop !== false;
    const aspectRatio = blockSettings.aspect_ratio || 'auto';
    const customHeight = blockSettings.custom_height || 400;
    const slideGap = blockSettings.slide_gap || 16;
    const slidesPerView = blockSettings.slides_per_view || 1;
    const slidesPerViewMobile = blockSettings.slides_per_view_mobile || 1;
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 10
    } : {};
    
    return (
        <Carousel
            slides={childBlocks}
            showNavigation={showNavigation}
            showPagination={showPagination}
            autoplay={autoplay}
            autoplaySpeed={autoplaySpeed}
            loop={loop}
            aspectRatio={aspectRatio}
            customHeight={customHeight}
            slideGap={slideGap}
            slidesPerView={slidesPerView}
            slidesPerViewMobile={slidesPerViewMobile}
            {...positionProps}
        />
    );
}

DateTimePickerComponent.displayName = 'theme__block-datetime-picker';
export function DateTimePickerComponent({ ...props }) {
    const DateTimePicker = lazyComponents['DateTimePicker'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract datetime picker settings
    const inputName = blockSettings.input_name || '';
    const inputId = blockSettings.input_id || '';
    const inputLabel = blockSettings.input_label || '';
    const inputPlaceholder = blockSettings.input_placeholder || '';
    const inputRequired = blockSettings.input_required === 'true' || blockSettings.input_required === true;
    const pickerType = blockSettings.picker_type || 'datetime'; // Can be 'date', 'time', or 'datetime'
    const minDate = blockSettings.min_date || '';
    const maxDate = blockSettings.max_date || '';
    const dateFormat = blockSettings.date_format || 'YYYY-MM-DD';
    const timeFormat = blockSettings.time_format || 'HH:mm';
    const disabledDays = blockSettings.disabled_days || []; // Array of disabled days, e.g. ['saturday', 'sunday']
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 10
    } : {};
    
    return (
        <DateTimePicker
            name={inputName}
            id={inputId}
            label={inputLabel}
            placeholder={inputPlaceholder}
            required={inputRequired}
            type={pickerType}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            disabledDays={disabledDays}
            {...positionProps}
        />
    );
}

MenuListComponent.displayName = 'theme__block-menu-list';
export function MenuListComponent({ ...props }) {
    const MenuList = lazyComponents['MenuList'];
    
    const blockId = props.settings.block_id;
    const block = props.settings.blocks.find((block) => block.id === blockId) || props.settings.block_settings ;
    
    const blockSettings = block.settings || block;
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract menu list settings
    const menuItems =  props.settings.additional || block.links || props.settings.links || [];
    const displayStyle = blockSettings.menu_display_type || 'vertical';
    const showIcons = blockSettings.show_icons !== false;
    const iconPosition = blockSettings.icon_position || 'left';
    const menuItemSpacing = blockSettings.menu_item_gap || 12;
    const hoverEffect = blockSettings.hover_effect || 'none';
    const textStyle = blockSettings.menu_item_style || 'b2';
    const textAlignment = blockSettings.text_alignment || 'left';
    const menuAlignment = blockSettings.menu_alignment || 'left';
    const customClass = blockSettings.custom_class || '';
    const enableHoverEvents = blockSettings.enable_hover_events || false;
    const hoverEventNamePrefix = blockSettings.hover_event_prefix || 'menu-hover';
    
    // Tailwind classes based on settings
    const containerClasses = [
        'flex',
        displayStyle === 'vertical' ? 'flex-col' : 'flex-row',
        customClass,
        `justify-${menuAlignment}`,
        `items-${menuAlignment}`,
        `gap-[${menuItemSpacing}px]`,
    ].filter(Boolean).join(' ');

    const itemClasses = [
        textStyle,
        `justify-${textAlignment}`,
        hoverEffect === 'underline' ? 'hover:underline' : '',
        hoverEffect === 'bold' ? 'hover:font-bold' : '',
        hoverEffect === 'color' ? 'hover:text-primary' : '',
    ].filter(Boolean).join(' ');
    
    // Function to handle hover events
    const handleHover = (event, link, isEnter) => {
        if (!enableHoverEvents || typeof window === 'undefined') return;
        
        const eventType = isEnter ? 'mouseenter' : 'mouseleave';
        const eventName = isEnter ? `${hoverEventNamePrefix}:enter` : `${hoverEventNamePrefix}:leave`;
        const linkIdentifier = link.handle || link.title || '';
        
        // Create a custom event with the menu item details
        try {
            window.dispatchEvent(
                new CustomEvent(eventName, {
                    detail: {
                        linkId: linkIdentifier,
                        linkTitle: link.title,
                        linkUrl: link.url,
                        eventType: eventType,
                        target: event.target
                    },
                    bubbles: true
                })
            );
            
            // Also dispatch a specific event for this particular menu item
            window.dispatchEvent(
                new CustomEvent(`${eventName}:${linkIdentifier}`, {
                    detail: {
                        linkId: linkIdentifier,
                        linkTitle: link.title,
                        linkUrl: link.url,
                        eventType: eventType,
                        target: event.target
                    },
                    bubbles: true
                })
            );

        // console.log('Hover event:', eventName, link, isEnter);
        } catch (error) {
            console.error('Error dispatching hover event:', error);
        }
    };

    return (
        <nav className={containerClasses}>
            {menuItems.map((link, idx) => (
                <a
                    key={link.handle || link.title || idx}
                    href={link.url || '#'}
                    className={itemClasses}
                    target={link.target || '_self'}
                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                    onMouseEnter={(e) => handleHover(e, link, true)}
                    onMouseLeave={(e) => handleHover(e, link, false)}
                    data-menu-item={link.handle || link.title || idx}
                >
                    {showIcons && link.icon && iconPosition === 'left' && (
                        <span className={`inline-block mr-2 align-middle`}>
                            <i className={link.icon}></i>
                        </span>
                    )}
                    <span className="align-middle">{link.title}</span>

                    {showIcons && link.icon && iconPosition === 'right' && (
                        <span className={`inline-block ml-2 align-middle`}>
                            <i className={link.icon}></i>
                        </span>
                    )}
                </a>
            ))}
        </nav>
    );
}

LiquidCodeComponent.displayName = 'theme__block-liquid-code';
export function LiquidCodeComponent({ ...props }) {
    // This component simply renders a container since the actual Liquid code
    // is processed server-side and the result is what gets sent to the client
    
    return (
        <div className="liquid-code-block w-full">
            {/* The processed Liquid code output would be rendered here */}
            <div dangerouslySetInnerHTML={{ __html: props.settings.html_content || '' }} />
        </div>
    );
}

ProductComponent.displayName = 'theme__block-product';
export function ProductComponent({ ...props }) {
    const Product = lazyComponents['Product'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings;
    const productData = props.settings.product;
    
    if (!blockSettings || !productData) {
        return null;
    }
    
    // Extract product display settings
    const displayMode = blockSettings.display_mode || 'card';
    const showTitle = blockSettings.show_title !== false;
    const showPrice = blockSettings.show_price !== false;
    const showVariants = blockSettings.show_variants !== false;
    const showDescription = blockSettings.show_description || false;
    const showAddToCart = blockSettings.show_add_to_cart !== false;
    const showQuantity = blockSettings.show_quantity || false;
    const imageAspectRatio = blockSettings.image_aspect_ratio || '4:3';
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockSettings.z_index || 10
    } : {};
    
    return (
        <Product
            product={productData}
            displayMode={displayMode}
            showTitle={showTitle}
            showPrice={showPrice}
            showVariants={showVariants}
            showDescription={showDescription}
            showAddToCart={showAddToCart}
            showQuantity={showQuantity}
            imageAspectRatio={imageAspectRatio}
            {...positionProps}
        />
    );
}

ProductTileComponent.displayName = 'theme__block-product-tile';
export function ProductTileComponent({ ...props }) {
    const ProductTile = lazyComponents['ProductTile'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings;
    const productData = props.settings.additional;
    
    // console.log('ProductTileComponent props:', props);
    // console.log('ProductTileComponent additional:', productData);
    if (!blockSettings || !productData) {
        return null;
    }
    
    // Extract product display settings
    const showPrice = blockSettings.show_price !== false;
    const showTags = blockSettings.show_tags !== false;
    const showQuickAdd = blockSettings.show_quickadd !== false;
    const enableWishlist = blockSettings.enable_wishlist || false;
    
    // console.log('ProductTileComponent props 2:', blockSettings);
    return (
        <ProductTile
            product={productData}
            settings={blockSettings}
            showPrice={showPrice}
            showTags={showTags}
            showQuickAdd={showQuickAdd}
            enableWishlist={enableWishlist}
            type={blockSettings.display_mode || 'tile'}
        />
    );
}

ScrollableComponent.displayName = 'theme__block-scrollable';
export function ScrollableComponent({ ...props }) {
    const [containerRef, setContainerRef] = React.useState(null);
    const [isAtStart, setIsAtStart] = React.useState(true);
    const [isAtEnd, setIsAtEnd] = React.useState(false);
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || {};
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract scrollable settings
    const scrollDirection = blockSettings.scroll_direction || 'vertical';
    const height = parseInt(blockSettings.height || 300, 10);
    const maxHeight = parseInt(blockSettings.max_height || 500, 10);
    const itemsGap = parseInt(blockSettings.items_gap || 16, 10);
    const hideScrollbar = blockSettings.hide_scrollbar || false;
    const showScrollIndicators = blockSettings.show_scroll_indicators !== false;
    const snapScroll = blockSettings.snap_scroll || false;
    const scrollSpeed = parseInt(blockSettings.scroll_speed || 5, 10) * 100; // Convert speed setting to pixels
    const visibleItems = parseInt(blockSettings.visible_items || 0, 10);
    const itemMinWidth = parseInt(blockSettings.item_min_width || 260, 10);
    
    // Create styles based on scroll direction
    const containerStyles = {
        height: scrollDirection === 'horizontal' ? `${height}px` : 
               scrollDirection === 'vertical' ? `${height}px` : 'auto',
        maxHeight: maxHeight > 0 ? `${maxHeight}px` : undefined,
    };
    
    // Create classes for the container
    const containerClasses = [
        'scrollable-component',
        'w-full',
        'relative'
    ].filter(Boolean).join(' ');
    
    // Create classes for the scrollable area
    const scrollableClasses = [
        'scrollable-area',
        scrollDirection === 'horizontal' ? 'overflow-x-auto overflow-y-hidden' : '',
        scrollDirection === 'vertical' ? 'overflow-y-auto overflow-x-hidden' : '',
        scrollDirection === 'both' ? 'overflow-auto' : '',
        hideScrollbar ? 'scrollbar-hide' : '',
        'w-full h-full'
    ].filter(Boolean).join(' ');
    
    // Create styles for the inner container
    const innerContainerStyles = {
        display: scrollDirection === 'horizontal' ? 'flex' : 'block',
        flexDirection: scrollDirection === 'horizontal' ? 'row' : undefined,
        gap: `${itemsGap}px`,
        width: scrollDirection === 'horizontal' ? 'max-content' : '100%',
    };
    
    // Create classes for the inner container
    const innerContainerClasses = [
        'scrollable-inner-container',
        scrollDirection === 'horizontal' && snapScroll ? 'snap-x snap-mandatory' : '',
    ].filter(Boolean).join(' ');
    
    // Create styles for the items wrapper
    const itemsWrapperStyles = {
        display: 'grid',
        gap: `${itemsGap}px`,
        gridAutoFlow: scrollDirection === 'horizontal' ? 'column' : 'row',
    };
    
    // Add grid template columns based on visible items setting
    if (scrollDirection === 'horizontal' && visibleItems > 0) {
        itemsWrapperStyles.gridTemplateColumns = `repeat(${visibleItems}, minmax(0, 1fr))`;
        itemsWrapperStyles.gridAutoColumns = `${itemMinWidth}px`;
    } else if (scrollDirection === 'vertical') {
        itemsWrapperStyles.gridTemplateColumns = '1fr';
    }
    
    // Update scroll position indicators on scroll
    React.useEffect(() => {
        if (!containerRef) return;
        
        const checkScrollPosition = () => {
            if (scrollDirection === 'horizontal') {
                setIsAtStart(containerRef.scrollLeft <= 0);
                setIsAtEnd(containerRef.scrollLeft + containerRef.clientWidth >= containerRef.scrollWidth - 5);
            } else if (scrollDirection === 'vertical') {
                setIsAtStart(containerRef.scrollTop <= 0);
                setIsAtEnd(containerRef.scrollTop + containerRef.clientHeight >= containerRef.scrollHeight - 5);
            }
        };
        
        containerRef.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition(); // Initial check
        
        // Re-check when window resizes or content changes
        window.addEventListener('resize', checkScrollPosition);
        const observer = new MutationObserver(checkScrollPosition);
        observer.observe(containerRef, { childList: true, subtree: true });
        
        return () => {
            containerRef.removeEventListener('scroll', checkScrollPosition);
            window.removeEventListener('resize', checkScrollPosition);
            observer.disconnect();
        };
    }, [containerRef, scrollDirection]);
    
    // Handle scroll controls
    const scroll = (direction) => {
        if (!containerRef) return;
        
        const scrollAmount = parseInt(blockSettings.scroll_amount || 300, 10);
        const scrollOptions = { behavior: 'smooth' };
        
        switch(direction) {
            case 'left':
                scrollOptions.left = -scrollAmount;
                break;
            case 'right':
                scrollOptions.left = scrollAmount;
                break;
            case 'up':
                scrollOptions.top = -scrollAmount;
                break;
            case 'down':
                scrollOptions.top = scrollAmount;
                break;
        }
        
        containerRef.scrollBy(scrollOptions);
    };
    
    // Extract positioning data for overlay support
    const positionType = blockSettings.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        position: 'absolute',
        top: `${blockSettings.position_top || 0}%`,
        left: `${blockSettings.position_left || 0}%`,
        right: blockSettings.position_right ? `${blockSettings.position_right}%` : 'auto',
        bottom: blockSettings.position_bottom ? `${blockSettings.position_bottom}%` : 'auto',
        zIndex: blockSettings.z_index || 10,
        transform: `${blockSettings.position_left && !blockSettings.position_right ? 'translateX(-50%)' : ''} ${blockSettings.position_top && !blockSettings.position_bottom ? 'translateY(-50%)' : ''}`.trim(),
    } : {};
    
    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });
    
    return (
        <div 
            ref={animation.ref} 
            style={{...positionProps, ...animation.style}} 
            className={containerClasses}
        >
            {showScrollIndicators && scrollDirection === 'horizontal' && (
                <div className="scroll-controls flex justify-between absolute top-1/2 -translate-y-1/2 left-0 right-0 z-10 px-2 pointer-events-none">
                    <button 
                        onClick={() => scroll('left')} 
                        className={`scroll-left p-2 rounded-full bg-gray-200 hover:bg-gray-300 pointer-events-auto transition-opacity ${isAtStart ? 'opacity-30 cursor-not-allowed' : 'opacity-80'}`}
                        disabled={isAtStart}
                    >
                        <span className="icon-arrow-left" aria-hidden="true"></span>
                        <span className="sr-only">Scroll left</span>
                    </button>
                    <button 
                        onClick={() => scroll('right')} 
                        className={`scroll-right p-2 rounded-full bg-gray-200 hover:bg-gray-300 pointer-events-auto transition-opacity ${isAtEnd ? 'opacity-30 cursor-not-allowed' : 'opacity-80'}`}
                        disabled={isAtEnd}
                    >
                        <span className="icon-arrow-right" aria-hidden="true"></span>
                        <span className="sr-only">Scroll right</span>
                    </button>
                </div>
            )}
            
            <div className="scrollable-wrapper relative w-full h-full">
                {showScrollIndicators && scrollDirection === 'vertical' && (
                    <button 
                        onClick={() => scroll('up')} 
                        className={`scroll-up absolute top-0 left-1/2 -translate-x-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10 transition-opacity ${isAtStart ? 'opacity-30 cursor-not-allowed' : 'opacity-80'}`}
                        disabled={isAtStart}
                    >
                        <span className="icon-arrow-up" aria-hidden="true"></span>
                        <span className="sr-only">Scroll up</span>
                    </button>
                )}
                
                <div 
                    ref={setContainerRef} 
                    className={scrollableClasses} 
                    style={containerStyles}>
                    <div className={innerContainerClasses} style={innerContainerStyles}>
                        <div className="scrollable-items-wrapper" style={itemsWrapperStyles}>
                            {/* The content will be rendered by the Liquid template */}
                            {props.settings.content ? (
                                <div dangerouslySetInnerHTML={{ __html: props.settings.content }} />
                            ) : props.children ? (
                                props.children
                            ) : (
                                <div className="scrollable-placeholder text-center p-4 text-gray-500">
                                   
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {showScrollIndicators && scrollDirection === 'vertical' && (
                    <button 
                        onClick={() => scroll('down')} 
                        className={`scroll-down absolute bottom-0 left-1/2 -translate-x-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10 transition-opacity ${isAtEnd ? 'opacity-30 cursor-not-allowed' : 'opacity-80'}`}
                        disabled={isAtEnd}
                    >
                        <span className="icon-arrow-down" aria-hidden="true"></span>
                        <span className="sr-only">Scroll down</span>
                    </button>
                )}
            </div>
        </div>
    );
}

DividerComponent.displayName = 'theme__block-divider';
export function DividerComponent({ ...props }) {
    const WaveDivider = lazyComponents['WaveDivider'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || {};
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract the divider settings
    const waveSettings = {
        wave_height: blockSettings.wave_height || 80,
        wave_color: blockSettings.wave_color || "#ffffff",
        wave_opacity: blockSettings.wave_opacity ? blockSettings.wave_opacity / 100.0 : 1,
        wave_style: blockSettings.wave_style || "wavy",
        flip_vertically: blockSettings.flip_vertically || false,
        flip_horizontally: blockSettings.flip_horizontally || false,
        hide_on_mobile: blockSettings.hide_on_mobile || false,
        z_index: blockSettings.z_index || 1,
        position: blockSettings.position || "bottom",
        render_type: blockSettings.render_type || "all",
        custom_id: blockSettings.custom_id || "",
        custom_class: blockSettings.custom_class || "",
        show_section_borders: blockSettings.show_section_borders || false,
        background_color: blockSettings.background_color || ""
    };
    
    return (
        <WaveDivider settings={waveSettings} />
    );
}


ProductSellingPlansBlockComponent.displayName = 'theme__block-product-sellingplans';
export function ProductSellingPlansBlockComponent({ ...props }) {
    const ProductSellingGroups = lazyComponents['ProductSellingGroups'];
    
    const blockSettings = props.settings.block_settings;
    const productData = props.settings.additional || props.settings.product;
    
    if (!blockSettings || !productData) {
        return null;
    }
    
    // Extract selling plans settings
    const title = blockSettings.title || 'Purchase Options';
    const oneTimeLabel = blockSettings.one_time_label || 'One-time purchase';
    const subscriptionLabel = blockSettings.subscription_label || 'Subscribe & Save';
    const showSavingsBadge = blockSettings.show_savings_badge !== false;
    const savingsText = blockSettings.savings_text || 'Save {percentage}%';
    const displayLayout = blockSettings.display_layout || 'radio';
    const showDescription = blockSettings.show_description !== false;
    const showPriceComparison = blockSettings.show_price_comparison !== false;
    const benefitsText = blockSettings.benefits_text || 'Skip, pause, or cancel anytime';
    
    return (
        <div className="product-selling-plans-block">
            <ProductSellingGroups
                product={productData}
                title={title}
                oneTimeLabel={oneTimeLabel}
                subscriptionLabel={subscriptionLabel}
                showSavingsBadge={showSavingsBadge}
                savingsText={savingsText}
                layout={displayLayout}
                showDescription={showDescription}
                showPriceComparison={showPriceComparison}
                benefitsText={benefitsText}
                settings={blockSettings}
            />
        </div>
    );
}

FindInStoreBlockComponent.displayName = 'theme__block-find-in-store';
export function FindInStoreBlockComponent({ ...props }) {
    const FindInStore = lazyComponents['FindInStore'];
    
    const blockSettings = props.settings.block_settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract find in store settings
    const title = blockSettings.title || 'Find in Store';
    const subtitle = blockSettings.subtitle || 'Check availability at your local store';
    const buttonText = blockSettings.button_text || 'Find Stores';
    const placeholderText = blockSettings.placeholder_text || 'Enter your location or ZIP code';
    const loadingText = blockSettings.loading_text || 'Searching stores...';
    const noResultsText = blockSettings.no_results_text || 'No stores found in your area';
    const errorText = blockSettings.error_text || 'Error finding stores. Please try again.';
    
    return (
        <div className="find-in-store-block">
            <FindInStore
                title={title}
                subtitle={subtitle}
                buttonText={buttonText}
                placeholderText={placeholderText}
                loadingText={loadingText}
                noResultsText={noResultsText}
                errorText={errorText}
                settings={blockSettings}
            />
        </div>
    );
}

BackInStockBlockComponent.displayName = 'theme__block-back-in-stock';
export function BackInStockBlockComponent({ ...props }) {
    const BackInStock = lazyComponents['BackInStock'];
    
    const blockSettings = props.settings.block_settings;
    
    if (!blockSettings) {
        return null;
    }
    
    // Extract back in stock settings
    const title = blockSettings.title || 'Get notified when back in stock';
    const subtitle = blockSettings.subtitle || "We'll send you an email when this product is available again";
    const buttonText = blockSettings.button_text || 'Notify Me';
    const successMessage = blockSettings.success_message || "Thanks! We'll notify you when this item is back in stock.";
    const placeholderText = blockSettings.placeholder_text || 'Enter your email address';
    const showOnlyWhenSoldOut = blockSettings.show_only_when_sold_out !== false;
    const buttonStyle = blockSettings.button_style || 'primary';
    const inputStyle = blockSettings.input_style || 'standard';
    
    return (
        <div className="back-in-stock-block">
            <BackInStock
                title={title}
                subtitle={subtitle}
                buttonText={buttonText}
                successMessage={successMessage}
                placeholderText={placeholderText}
                showOnlyWhenSoldOut={showOnlyWhenSoldOut}
                buttonStyle={buttonStyle}
                inputStyle={inputStyle}
                settings={blockSettings}
            />
        </div>
    );
}

BadgeComponent.displayName = 'theme__block-badge';
export function BadgeComponent({ ...props }) {
    const Paragraph = lazyComponents['Paragraph'];
    const blockData = props.settings.block_settings;

    const text = blockData.text;
    const style = blockData.text_style;
    const htmlTag = blockData.html_tag || 'div'; // Get the HTML tag for SEO

    // Extract positioning data for overlay support
    const positionType = blockData.position_type || 'relative';
    const positionProps = positionType === 'absolute' ? {
        isOverlay: true,
        zIndex: blockData.z_index || 10
    } : {};
   
    // Animation settings
    const enableAnimations = blockData.enable_animations || false;
    const animationType = blockData.animation_type || 'slide-fade-up';
    const animationDuration = blockData.animation_duration || 800;
    const animationDelay = blockData.animation_delay || 0;
    const animationTrigger = blockData.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });

    return (
        <div ref={animation.ref}>
            <Paragraph    
                text={text}
                type={style}
                htmlTag={htmlTag}
                shouldTruncateText={true}
                {...positionProps}
            />
        </div>
    );
}

OffsetImagesComponent.displayName = 'theme__block-offset-images';
export function OffsetImagesComponent({ ...props }) {
    const OffsetImages = lazyComponents['OffsetImages'];
    
    const blockId = props.settings.block_id;
    const blockSettings = props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
    
    if (!blockSettings) {
        return null;
    }

    // Extract image settings
    const primaryImageUrl = blockSettings.primary_image;
    const secondaryImageUrl = blockSettings.secondary_image;
    const imageFit = blockSettings.image_fit || 'cover';
    const aspectRatio = blockSettings.aspect_ratio || 'auto';
    const overlayOpacity = blockSettings.overlay_opacity || 0;

    const secondaryTop = blockSettings.secondary_top || 0;
    const secondaryLeft = blockSettings.secondary_left || 0;
    
    // Animation settings
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    // Use the animation hook
    const animation = useAnimation({
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger
    });

    return (
        <div ref={animation.ref} style={animation.style}>
            <OffsetImages
                primaryImageUrl={primaryImageUrl}
                secondaryImageUrl={secondaryImageUrl}
                fit={imageFit}
                ratio={aspectRatio}
                overlay={overlayOpacity > 0}
                overlayOpacity={overlayOpacity / 100}
                classes={blockSettings.width < 100 ? `w-[${blockSettings.width}%]` : 'w-full'}
                secondaryTop={secondaryTop}
                secondaryLeft={secondaryLeft}
            />
        </div>
    );
}